import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Container } from 'semantic-ui-react';
import { Copy, Check, ChevronRight, Award } from 'lucide-react';
import TeaserImages from '../Helpers/TeaserImages';
import AuthorsLink from '../Helpers/AuthorsLink';
import RelatedItemsDisplay from '../Helpers/RelatedItemsDisplay';
import { resolvePublicationAuthors } from '../../../helpers/resolvePublicationAuthors';
import Sections from '../Helpers/Sections';
import DefaultViewWithoutTitle from '../../Custom/DefaultViewWithoutTitle';
import { activePublicationLinks } from '../../../helpers/publicationLinks';
import { flattenToAppURL } from '@plone/volto/helpers/Url/Url';
import '../academicons/css/academicons.min.css';
import {
  hasImage,
  normalizePerson,
  vocabTitle,
} from '../../../helpers/content';
import { flattenRichTextToAppURL } from '../../../helpers/richText';

const PublicationView = (props) => {
  const { content } = props;
  const displayAuthors = resolvePublicationAuthors(content)
    .map(normalizePerson)
    .filter(Boolean);

  const normalizeProject = (project) => {
    if (!project) return null;
    if (typeof project === 'string') {
      return { title: project, '@id': null };
    }
    return project;
  };
  const displayRelatedProjects = Array.isArray(content.related_projects)
    ? content.related_projects.map(normalizeProject).filter(Boolean)
    : [];

  const [bibtexCopied, setBibtexCopied] = useState(false);
  const [bibtexOpen, setBibtexOpen] = useState(false);
  const publicationAbstract = content.publication_abstract;
  const publicationPublishedIn = content.publication_published_in;
  const publicationFunding = content.publication_funding;
  const publicationYear = content.publication_year;
  const publicationYearLabel = vocabTitle(publicationYear);
  // See FlexPublicationCard.jsx's copy of this check: some "published in"
  // citations already end in the year (often the month too), so showing
  // the separate Year field would just repeat it. Other sites built on
  // this same add-on write "published in" without a year, where the field
  // is the only source of it, so this has to stay a per-item check.
  const yearAlreadyInPublishedIn =
    !!publicationPublishedIn &&
    !!publicationYearLabel &&
    String(publicationPublishedIn).includes(String(publicationYearLabel));
  const publicationDoi = content.publication_doi;
  const publicationBibtex = content.publication_bibtex_string;
  const teaserImage1 = content.teaser_image1;
  const teaserImage2 = content.teaser_image2;

  const typeLabel = vocabTitle(content.type_of_publication) || null;

  const hasImages = hasImage(teaserImage1) || hasImage(teaserImage2);

  const hasAbstract =
    publicationAbstract?.data && publicationAbstract.data !== '<p></p>';

  const hasMetaLeft =
    publicationPublishedIn ||
    publicationFunding ||
    content.funder_name ||
    content.grant_id ||
    content.funding_program;
  const showYear = publicationYearLabel && !yearAlreadyInPublishedIn;
  const hasMetaRight = showYear || publicationDoi || content.funding_period;
  const hasMeta = hasMetaLeft || hasMetaRight;

  const hasHighlights = content.is_open_access || content.is_best_paper;

  const hasRelatedProjects = displayRelatedProjects.length > 0;

  const hasRelatedTopics =
    Array.isArray(content.related_topics) && content.related_topics.length > 0;

  // Funding Link first, then the publication's own resources and videos.
  const links = [
    ...(content.funding_link
      ? [
          {
            key: 'funding_link',
            label: 'Funding Link',
            href: flattenToAppURL(content.funding_link),
          },
        ]
      : []),
    ...activePublicationLinks(content),
  ];
  const hasLinks = links.length > 0;

  const hasBibtex =
    publicationBibtex?.data && publicationBibtex.data !== '<p></p>';

  const bibtexFormatted = publicationBibtex?.data
    ? publicationBibtex.data.replace(/<p>/g, '').replace(/<\/p>/g, '<br />')
    : '';

  const hasRelatedProjectsVisible =
    hasRelatedProjects && !content.hide_related_projects;
  const hasRelatedCompetitions =
    content.related_competitions?.length > 0 &&
    !content.hide_related_competitions;

  return (
    <Container id="view-wrapper" className="PublicationView">
      {/* Header */}
      <div className="view-header">
        <div className="view-header__badges">
          {typeLabel && (
            <span className="view-header__subtitle">{typeLabel}</span>
          )}
        </div>
        <h2 className="view-header__title">
          {content.title ? content.title : content.id}
        </h2>
        {displayAuthors.length > 0 && (
          <p className="view-header__authors">
            <AuthorsLink authors={displayAuthors} inline />
          </p>
        )}
        <div className="view-header__divider" />
      </div>
      <Sections>
        {/* Teaser Images */}
        {hasImages && <TeaserImages content={content} />}

        {/* Abstract */}
        {hasAbstract && (
          <>
            <div className="view-section">
              <h3 className="view-section__label">Abstract:</h3>
              <div
                className="view-section__abstract"
                dangerouslySetInnerHTML={{
                  __html: flattenRichTextToAppURL(publicationAbstract.data),
                }}
              />
            </div>
          </>
        )}

        {/* 2-Column Metadata */}
        {hasMeta && (
          <>
            <div className="view-meta-grid">
              <div className="view-meta-grid__col">
                {publicationPublishedIn && (
                  <div className="view-section">
                    <h3 className="view-section__label">Published in:</h3>
                    <p className="view-section__value">
                      {publicationPublishedIn}
                    </p>
                  </div>
                )}
                {publicationFunding && (
                  <div className="view-section">
                    <h3 className="view-section__label">Funding:</h3>
                    <p className="view-section__value">{publicationFunding}</p>
                  </div>
                )}
                {content.funder_name && (
                  <div className="view-section">
                    <h3 className="view-section__label">Funder:</h3>
                    <p className="view-section__value">{content.funder_name}</p>
                  </div>
                )}
                {content.grant_id && (
                  <div className="view-section">
                    <h3 className="view-section__label">Grant ID:</h3>
                    <p className="view-section__value">{content.grant_id}</p>
                  </div>
                )}
                {content.funding_program && (
                  <div className="view-section">
                    <h3 className="view-section__label">Funding Program:</h3>
                    <p className="view-section__value">
                      {content.funding_program}
                    </p>
                  </div>
                )}
              </div>
              <div className="view-meta-grid__col">
                {showYear && (
                  <div className="view-section">
                    <h3 className="view-section__label">Year:</h3>
                    <p className="view-section__value">
                      {publicationYearLabel}
                    </p>
                  </div>
                )}
                {publicationDoi && (
                  <div className="view-section">
                    <h3 className="view-section__label">DOI:</h3>
                    <p className="view-section__value">
                      <a
                        href={publicationDoi}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {publicationDoi}
                      </a>
                    </p>
                  </div>
                )}
                {content.funding_period && (
                  <div className="view-section">
                    <h3 className="view-section__label">Funding Period:</h3>
                    <p className="view-section__value">
                      {content.funding_period}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {/* BibTeX */}
        {hasBibtex && (
          <>
            <div className="bibtex-box">
              <div
                className="bibtex-box__header"
                onClick={() => setBibtexOpen(!bibtexOpen)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) =>
                  e.key === 'Enter' && setBibtexOpen(!bibtexOpen)
                }
              >
                <span className="bibtex-box__title">
                  <ChevronRight
                    size={14}
                    className={
                      'bibtex-box__chevron' +
                      (bibtexOpen ? ' bibtex-box__chevron--open' : '')
                    }
                  />
                  BibTeX
                </span>
                <button
                  className="bibtex-box__copy-btn"
                  title={bibtexCopied ? 'Copied!' : 'Copy BibTeX'}
                  onClick={(e) => {
                    e.stopPropagation();
                    const plain = publicationBibtex.data
                      .replace(/<[^>]+>/g, '')
                      .replace(/&amp;/g, '&')
                      .replace(/&lt;/g, '<')
                      .replace(/&gt;/g, '>')
                      .replace(/&quot;/g, '"')
                      .trim();
                    navigator.clipboard.writeText(plain).then(() => {
                      setBibtexCopied(true);
                      setTimeout(() => setBibtexCopied(false), 2000);
                    });
                  }}
                >
                  {bibtexCopied ? <Check size={14} /> : <Copy size={14} />}
                  <span>{bibtexCopied ? 'Copied!' : 'Copy'}</span>
                </button>
              </div>
              {bibtexOpen && (
                <pre
                  className="bibtex-box__content"
                  dangerouslySetInnerHTML={{
                    __html: flattenRichTextToAppURL(bibtexFormatted),
                  }}
                />
              )}
            </div>
          </>
        )}

        {/* Highlights (Open Access / Best Paper Award) */}
        {hasHighlights && (
          <>
            <div className="view-section">
              <h3 className="view-section__label">Highlights:</h3>
              <div className="view-section__buttons">
                {content.is_open_access && (
                  <span className="view-section__highlight" title="Open Access">
                    <i
                      className="ai ai-open-access view-section__highlight-icon"
                      aria-hidden="true"
                    />
                    <span>Open Access</span>
                  </span>
                )}
                {content.is_best_paper && (
                  <span
                    className="view-section__highlight"
                    title="Best Paper Award"
                  >
                    <Award
                      size={16}
                      className="view-section__highlight-icon"
                      aria-hidden="true"
                    />
                    <span>Best Paper Award</span>
                  </span>
                )}
              </div>
            </div>
          </>
        )}

        {/* Links */}
        {hasLinks && (
          <>
            <div className="view-section">
              <h3 className="view-section__label">Links:</h3>
              <div className="view-section__buttons">
                {links.map(({ key, label, title, href }) => (
                  <a
                    key={key}
                    href={href}
                    title={title}
                    className="view-section__button"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {label}
                  </a>
                ))}
              </div>
            </div>
          </>
        )}

        {/* Related Projects */}
        {hasRelatedProjectsVisible && (
          <>
            <div className="view-section">
              <h3 className="view-section__label">Related Projects:</h3>
              <RelatedItemsDisplay
                items={displayRelatedProjects}
                mode={content.related_projects_display}
                kind="plain"
                fieldKey={`${content['@id']}-related_projects`}
              />
            </div>
          </>
        )}

        {/* Related Competitions */}
        {hasRelatedCompetitions && (
          <>
            <div className="view-section">
              <h3 className="view-section__label">Related Competitions:</h3>
              <RelatedItemsDisplay
                items={content.related_competitions}
                mode={content.related_competitions_display}
                kind="plain"
                fieldKey={`${content['@id']}-related_competitions`}
              />
            </div>
          </>
        )}

        {/* Related Topics */}
        {hasRelatedTopics && !content.hide_related_topics && (
          <>
            <div className="view-section">
              <h3 className="view-section__label">Related Topics:</h3>
              <RelatedItemsDisplay
                items={content.related_topics}
                mode={content.related_topics_display}
                kind="plain"
                fieldKey={`${content['@id']}-related_topics`}
              />
            </div>
          </>
        )}
      </Sections>

      {/* The block-rendered part of the page */}
      <DefaultViewWithoutTitle {...props} />
    </Container>
  );
};

PublicationView.propTypes = {
  content: PropTypes.shape({
    title: PropTypes.string,
    id: PropTypes.string,
    type_of_publication: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({ title: PropTypes.string }),
    ]),
    hide_related_projects: PropTypes.bool,
    hide_related_topics: PropTypes.bool,
    related_authors_display: PropTypes.string,
    related_projects_display: PropTypes.string,
    related_topics_display: PropTypes.string,
    is_open_access: PropTypes.bool,
    is_best_paper: PropTypes.bool,
    related_authors: PropTypes.arrayOf(
      PropTypes.shape({
        '@id': PropTypes.string,
        '@type': PropTypes.string,
        review_state: PropTypes.string,
        is_linkable: PropTypes.bool,
        publication_name: PropTypes.string,
        full_name: PropTypes.string,
        title: PropTypes.string,
      }),
    ),
    teaser_image1: PropTypes.object,
    teaser_image1_caption: PropTypes.string,
    teaser_image1_copyright: PropTypes.string,
    teaser_image2: PropTypes.object,
    teaser_image2_caption: PropTypes.string,
    teaser_image2_copyright: PropTypes.string,
    funder_name: PropTypes.string,
    grant_id: PropTypes.string,
    funding_program: PropTypes.string,
    funding_period: PropTypes.string,
    funding_link: PropTypes.string,
    related_projects: PropTypes.arrayOf(
      PropTypes.shape({ '@id': PropTypes.string, title: PropTypes.string }),
    ),
    related_topics: PropTypes.arrayOf(
      PropTypes.shape({ '@id': PropTypes.string, title: PropTypes.string }),
    ),
  }).isRequired,
};

export default PublicationView;
